//#region types
import { StyleEnums } from "models/enums";
import { MultiSearchInputProps, SelectInputProps, TextInputProps } from "models/propTypes/wintFormPropsTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { SendMailFormFieldsProps } from "models/propTypes/invoicePropTypes";
import { WintFormKey } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  HorizontalFormField,
  TextAreaInput,
  TextInput,
  SelectInput,
  MultiSearchInput,
  VerticalFormField,
} from "components/common/wintForms";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import useInvoicePreviewFormHelpers from "../useInvoicePreviewFormHelpers";

function SendMailFormFields<T extends Record<string, any>>(props: SendMailFormFieldsProps<T>) {
  const { invoiceId, mailOptions, isNotSendForm, isQuotation, parentFieldName, vertical, connect, fieldsLoading } =
    props;
  const { serialNumberTemplateValueDisplayText } = useInvoicePreviewFormHelpers();

  const connetToEmailFrom = connect(`${parentFieldName}.emailFromType` as WintFormKey<T>);
  const connectToEmailFromAddress = connect(`${parentFieldName}.emailFrom` as WintFormKey<T>);

  const emailFromMessage = connetToEmailFrom.value
    ? connetToEmailFrom.value === WintSuperkollAPIModel.SendInvoiceMailsFromEnum.Superkoll
      ? connetToEmailFrom.touched
        ? {
            type: StyleEnums.DisplayType.Success,
            message: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.MailSendFromSuccess),
          }
        : undefined
      : {
          type: StyleEnums.DisplayType.Warning,
          message: LocalizationHelperInstance.getFormattedMessage(
            isQuotation ? TranslationKeys.Quotation.MailSendFromWarning : TranslationKeys.Invoice.MailSendFromWarning
          ),
        }
    : undefined;

  const onEmailFromChange = (value?: WintSuperkollAPIModel.SendInvoiceMailsFromEnum, shouldValidate?: boolean) => {
    connetToEmailFrom.setValue(value, shouldValidate);
    connectToEmailFromAddress.setValue(mailOptions?.mailFromList?.find((m) => m.value === value)?.label, false);
  };

  const FieldComponent = vertical ? VerticalFormField : HorizontalFormField;

  return (
    <>
      <FieldComponent<SelectInputProps>
        Component={SelectInput}
        {...connetToEmailFrom}
        setValue={onEmailFromChange}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendEmailFrom)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Invoice.SendMailFromDescription
        )}
        items={
          mailOptions?.mailFromList?.filter(
            (m) => !isNotSendForm || m.value !== WintSuperkollAPIModel.SendInvoiceMailsFromEnum.PersonMail
          ) ?? []
        }
        customMessage={emailFromMessage}
        loading={fieldsLoading}
      />
      <FieldComponent<MultiSearchInputProps>
        Component={MultiSearchInput}
        {...connect(`${parentFieldName}.emailTo` as WintFormKey<T>)}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendEmailTo)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendMailToDescription)}
        items={mailOptions?.mailToList ?? []}
        valueLoading={fieldsLoading}
        freeSolo
      />
      <FieldComponent<TextInputProps>
        Component={TextInput}
        {...connect(`${parentFieldName}.emailSubject` as WintFormKey<T>)}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendEmailSubject)}
        descriptionText={
          isQuotation
            ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Quotation.SendMailSubjectDescription, "", {
                serialNumber: `${invoiceId ?? 0}`,
                templateText: serialNumberTemplateValueDisplayText,
              })
            : `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SendMailSubjectDescription)}`
        }
        isLoading={fieldsLoading}
      />
      <FieldComponent<TextInputProps>
        Component={TextAreaInput}
        {...connect(`${parentFieldName}.emailMessage` as WintFormKey<T>)}
        label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Message)}
        descriptionText={LocalizationHelperInstance.getFormattedMessage(
          TranslationKeys.Invoice.SendMailMessageDescription
        )}
        icon={IconDefinitionConstants.WCommentDots}
        isLoading={fieldsLoading}
      />
    </>
  );
}

export default SendMailFormFields;
